import _ from 'lodash';

import formEditorConstants from './constants';
import { FORM_STATUS, FORM_EDITOR_MODE } from '../../common/globals';
import utils from './utils';

const initialState = {
  isInitializing: true,
  isSaving: false,
  isPublishing: false,
  isDeleting: false,
  isDeleted: false,
  isModified: false,
  mode: FORM_EDITOR_MODE.EDIT_FORM,
  selectedElement: null,
  teamName: '',
  form: {
    id: '',
    status: FORM_STATUS.DRAFT,
    assignTasksTo: [],
    tasksDeadline: '',
    taskSummary: '',
    headerImage: '',
    confirmationText: '',
    buttonLabel: 'Submit',
    buttonColor: '',
    JSONSchema: {
      title: '',
      description: '',
      type: 'object'
    },
    UISchema: {
      'ui:order': []
    }
  },
  pristineForm: {
    assignTasksTo: [],
    tasksDeadline: '',
    taskSummary: '',
    headerImage: '',
    confirmationText: '',
    buttonLabel: 'Submit',
    buttonColor: '',
    JSONSchema: {
      title: '',
      description: '',
      type: 'object'
    },
    UISchema: {
      'ui:order': []
    }
  }
};

const formEditorReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case formEditorConstants.INITIALIZE_FORM_EDITOR:
      return _.merge({}, state, { isInitializing: true });

    case formEditorConstants.INITIALIZE_FORM_EDITOR_SUCCESS:
    {
      const updatedState = utils.initializeFormSuccess(state, payload);
     
      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_SAVE:
    {
      return _.merge({}, state, { isSaving: true });
    }

    case formEditorConstants.FORM_SAVE_SUCCESS:
    {
      const updatedState = utils.saveForm(state);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_SAVE_ERROR:
    {
      return _.merge({}, state, { isSaving: false });
    }

    case formEditorConstants.FORM_DELETE:
    {
      return _.merge({}, state, { isDeleting: true });
    }

    case formEditorConstants.FORM_DELETE_SUCCESS:
    {
      return _.merge({}, state, {
        isDeleting: false,
        isDeleted: true,
        form: {
          status: FORM_STATUS.DELETED
        }
      });
    }

    case formEditorConstants.FORM_DELETE_ERROR:
    {
      return _.merge({}, state, { isDeleting: false });
    }

    case formEditorConstants.FORM_PUBLISH:
    {
      return _.merge({}, state, { isPublishing: true });
    }

    case formEditorConstants.FORM_PUBLISH_SUCCESS:
    {
      return _.merge({}, state, {
        isPublishing: false,
        form: {
          status: FORM_STATUS.LIVE
        }
      });
    }

    case formEditorConstants.FORM_PUBLISH_ERROR:
    {
      return _.merge({}, state, {
        isPublishing: false
      });
    }

    case formEditorConstants.INITIALIZE_FORM_EDITOR_ERROR:
      return _.merge({}, state, { isInitializing: false });
    
    case formEditorConstants.FORM_EDITOR_MODE_CHANGE:
    {
      const updatedState = utils.updateEditorMode(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_SELECTED:
    {
      const selectedFromPayload = _.cloneDeep(payload.selectedElement);
      const elementId = selectedFromPayload && selectedFromPayload.id;
      if (elementId) {
        const hint = _.get(state, ['form', 'UISchema', elementId, 'ui:placeholder'], '');
        selectedFromPayload.uiPlaceholder = hint;
      }
      return _.extend({}, state, { selectedElement: selectedFromPayload });
    }
    
    case formEditorConstants.FORM_ELEMENT_CHANGE:
    {
      const updatedState = utils.updateFormElement(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_TYPE_CHANGE:
    {
      const updatedState = utils.updateFormElementType(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_MOVE_UP:
    {
      const updatedState = utils.moveFormElementUp(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_MOVE_DOWN:
    {
      const updatedState = utils.moveFormElementDown(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_REMOVE:
    {
      const updatedState = utils.removeFormElement(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_PREVIEW_CHANGE:
    {
      const updatedForm = _.cloneDeep(state.form);
      const { formData } = updatedForm;
      const { formData: updatedFormData } = payload;

      updatedForm.formData = _.extend({}, formData, updatedFormData);

      return _.extend({}, state, { form: updatedForm });
    }

    case formEditorConstants.FORM_DETAILS_CHANGE:
    {
      const updatedState = utils.updateFormDetails(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_DETAILS_ADD_ELEMENT:
    {
      const updatedState = utils.generateElement(state, payload);
      
      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_REQUIRED_CHANGE:
    {
      const updatedState = utils.changeRequiredFieldStatus(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_ELEMENT_OPTIONS_CHANGE:
    {
      const updatedState = utils.changeElementOptions(state, payload);

      return _.extend({}, state, updatedState);
    }

    case formEditorConstants.FORM_UNLOAD:
    {
      return _.extend({}, initialState);
    }

    case formEditorConstants.FORM_PRIVACY_CHANGE:
    {
      return _.extend({}, state, { isSaving: true });
    }

    case formEditorConstants.FORM_PRIVACY_CHANGE_SUCCESS:
    {
      const {
        privacy
      } = payload;

      return _.merge({}, state, {
        isSaving: false,
        form: {
          privacy
        }
      });
    }

    case formEditorConstants.FORM_PRIVACY_CHANGE_ERROR:
    {
      return _.extend({}, state, { isSaving: false });
    }

    case formEditorConstants.FORM_TASK_ASSIGNEES_CHANGE:
    {
      const {
        payload: {
          assignees
        }
      } = action;

      const updatedState = utils.updateTasksAssignedTo(state, assignees);

      return updatedState;
    }

    case formEditorConstants.FORM_TASK_DEADLINE_CHANGE:
    {
      const {
        payload: {
          deadline
        }
      } = action;

      const updatedState = utils.updateTaskDeadline(state, deadline);

      return updatedState;
    }

    case formEditorConstants.FORM_TASK_SUMMARY_CHANGE:
    {
      const {
        payload: {
          taskSummary
        }
      } = action;

      const updatedState = utils.updateTaskSummary(state, taskSummary);

      return updatedState;
    }

    case formEditorConstants.FORM_HEADER_IMAGE_CHANGE:
    {
      const {
        payload: {
          headerImage
        }
      } = action;

      const updatedState = utils.updateHeaderImage(state, headerImage);

      return updatedState;
    }

    case formEditorConstants.FORM_CONFIRMATION_TEXT_CHANGE:
    {
      const {
        payload: {
          confirmationText
        }
      } = action;

      const updatedState = utils.updateConfirmationText(state, confirmationText);

      return updatedState;
    }

    case formEditorConstants.FORM_BUTTON_LABEL_CHANGE:
    {
      const {
        payload: {
          buttonLabel
        }
      } = action;

      const updatedState = utils.updateButtonLabel(state, buttonLabel);

      return updatedState;
    }

    case formEditorConstants.FORM_BUTTON_COLOR_CHANGE:
    {
      const {
        payload: {
          buttonColor
        }
      } = action;

      const updatedState = utils.updateButtonColor(state, buttonColor);

      return updatedState;
    }

    default:
      return state;
  }
};

export default formEditorReducer;
